import { Fragment, type ReactNode } from 'react';
import { Divider, Stack, useTheme } from '@mui/material';

import type { LeaseDetailsFormConfig } from '../config';
import { ApplicantsSection } from '../sections/applicants';
import { CoverageSection } from '../sections/coverage';
import { LeaseInformationSection } from '../sections/lease-information';
import { ScreeningSection } from '../sections/screening';
import { useVisibilityRule } from '../hooks';

export type SectionsRendererProps = {
  config: LeaseDetailsFormConfig;
};

type VisibleSection = {
  key: string;
  node: ReactNode;
  hideDivider: boolean;
};

// Visibility resolution lives in a child component so the `useVisibilityRule`
// hooks (which `useWatch` under the hood) run inside the surrounding
// `<FormProvider>`. Hoisting them into `LeaseDetailsForm`'s body would call
// `useFormContext()` before the provider mounts and crash with a null context.
export const SectionsRenderer = ({ config }: SectionsRendererProps) => {
  const theme = useTheme();
  const leaseInformationVisible = useVisibilityRule(config.sections.leaseInformation.visible);
  const applicantsVisible = useVisibilityRule(config.sections.applicants.visible);
  const screeningVisible = useVisibilityRule(config.sections.screening.visible);
  const coverageVisible = useVisibilityRule(config.sections.coverage.visible);

  const visibleSections: VisibleSection[] = [
    leaseInformationVisible && {
      key: 'leaseInformation',
      node: <LeaseInformationSection config={config.sections.leaseInformation} />,
      hideDivider: config.sections.leaseInformation.hideDivider ?? false,
    },
    applicantsVisible && {
      key: 'applicants',
      node: <ApplicantsSection config={config.sections.applicants} />,
      hideDivider: config.sections.applicants.hideDivider ?? false,
    },
    screeningVisible && {
      key: 'screening',
      node: <ScreeningSection config={config.sections.screening} />,
      hideDivider: config.sections.screening.hideDivider ?? false,
    },
    coverageVisible && {
      key: 'coverage',
      node: <CoverageSection config={config.sections.coverage} />,
      hideDivider: config.sections.coverage.hideDivider ?? false,
    },
  ].filter(Boolean) as VisibleSection[];

  const sectionDivider = config.ui?.sectionDivider ?? (
    <Divider sx={{ borderColor: theme.palette.neutralGrey[400] }} />
  );
  const betweenSections = config.ui?.spacing?.betweenSections ?? 3;

  return (
    <Stack direction="column" spacing={betweenSections}>
      {visibleSections.map((section, index) => (
        <Fragment key={section.key}>
          {index > 0 && !section.hideDivider && sectionDivider}
          {section.node}
        </Fragment>
      ))}
    </Stack>
  );
};
